import { FC, ReactElement, useState, useRef, MouseEvent } from 'react'
import { useRouter } from 'next/router'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import ReCAPTCHA from 'react-google-recaptcha'
import {
  EmailShareButton,
  EmailIcon,
  TwitterShareButton,
  TwitterIcon,
  FacebookShareButton,
  FacebookIcon,
  WhatsappShareButton,
  WhatsappIcon
} from 'react-share'
import classNames from 'classnames'
import parse from 'html-react-parser'
import { routes } from 'utils/routes'
import { getReferralCodeByEmail } from 'api/customerFlow'
import { Email } from 'constants/regex'
import LoadingDots from 'components/loading-dots/LoadingDots'
import Button from 'components/button/Button'
import styles from './ReferralForm.module.scss'

const ReferralForm: FC = (): ReactElement => {
  // I18N
  const { t } = useTranslation(['referral', 'commonFormFields', 'validation'])
  const router = useRouter()
  const { locale: language } = router

  // Captcha
  const captchaRef = useRef<ReCAPTCHA>(null)

  // React Hook Form
  const { register, handleSubmit, formState } = useForm()
  const { errors } = formState

  // State
  const [submitted, setSubmitted] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)
  const [noEmailFound, setNoEmailFound] = useState(false)
  const [referralLink, setReferralLink] = useState('')
  const [copied, setCopied] = useState(false)

  // Set sharing message
  const messageShare = t(
    'messageShare',
    'Ik ben overgestapt naar Bolt! Kies ook voor een lokale energie-opwekker en samen maken we kans op 2 Cowboy fietsen.'
  )
  const messageShareTitle = t('messageShareTitle', "Bolt geeft 2 Cowboy e-bikes weg per 50 deelnemende duo's")

  /**
   * Handles the form submit after validation
   *
   * @param data
   */
  const onSubmit = async (data: Record<string, any>) => {
    if (!loading) {
      setLoading(true)

      // Execute CAPTCHA (returns token on success)
      const token = captchaRef.current && (await captchaRef.current.executeAsync())

      if (token) {
        try {
          const result = await getReferralCodeByEmail(data.email)
          const { found } = result

          if (!found) {
            setNoEmailFound(true)
          } else {
            const { referralCode } = result
            const host = window.location.origin
            const link = `${host}${routes(language).referral}?referralcode=${referralCode}`

            setReferralLink(link)
          }
        } catch (error) {
          setError(true)
        } finally {
          setSubmitted(true)
        }
      }

      setLoading(false)
    }
  }

  /**
   * Resets the form
   */
  const handleTryAgain = () => {
    setSubmitted(false)
    setNoEmailFound(false)
    setError(false)
  }

  /**
   * Copies the referral link
   */
  const handleCopyReferralLink = (e: MouseEvent) => {
    e.preventDefault()
    navigator.clipboard.writeText(referralLink)
    setCopied(true)
  }

  if (!process.env.NEXT_PUBLIC_GOOGLE_RECAPTCHA_KEY) {
    throw new Error('Google reCAPTCHA key missing!')
  }

  return (
    <div className={styles['referral-form']}>
      <h2>
        {referralLink
          ? t('formTitleReferred', 'Deel jouw unieke doorverwijzingslink en doe mee!')
          : t('formTitle', 'Geef jouw vriend jouw persoonlijke link om zich te registreren bij Bolt')}
      </h2>
      {!submitted ? (
        <>
          <form onSubmit={handleSubmit(onSubmit)}>
            <ReCAPTCHA ref={captchaRef} size="invisible" sitekey={process.env.NEXT_PUBLIC_GOOGLE_RECAPTCHA_KEY} />
            <div className={styles['form-input']}>
              <input
                {...register('email', { required: true, maxLength: 255, pattern: Email })}
                id="email"
                type="email"
                required
                className="border"
                placeholder={t('commonFormFields:email', 'E-mailadres')}
              />
              <Button withGradient extraClasses={classNames(styles.btn, { loading })}>
                {!loading ? t('formButton', 'Genereer jouw link') : <LoadingDots />}
              </Button>
            </div>
          </form>
          {errors.email && (
            <p className="is-red">
              {errors.email.type === 'required' && t('validation:required', 'Dit veld is verplicht.')}
              {errors.email.type === 'pattern' && t('validation:email', 'Gelieve een geldig e-mailadres in te vullen.')}
            </p>
          )}
        </>
      ) : (
        <div className={styles.feedback}>
          {noEmailFound && (
            <div className={styles['error-message']}>
              <p>
                {parse(
                  t(
                    'errorNoEmailFound',
                    '<strong>We konden geen link genereren voor het opgegeven e-mailadres.</strong><br />Zorg er voor dat je eerst zelf stroom afneemt via Bolt.'
                  )
                )}
              </p>
              <Button withGradient onClick={() => handleTryAgain()} extraClasses={styles.btn}>
                {t('buttonTryAgain', 'Probeer het opnieuw')}
              </Button>
            </div>
          )}
          {error && (
            <div className={styles['error-message']}>
              <p>
                {parse(
                  t(
                    'errorSomethingWentWrong',
                    '<strong>Er ging iets mis.</strong> Gelieve het opnieuw te proberen of contacteer ons op <a href="tel:028993300">02 899 33 00</a> of stuur een e-mail naar <a href="mailto:hallo@boltenergie.be">hallo@boltenergie.be</a>.'
                  )
                )}
              </p>
              <Button withGradient onClick={() => handleTryAgain()} extraClasses={styles.btn}>
                {t('buttonTryAgain', 'Probeer het opnieuw')}
              </Button>
            </div>
          )}
          {referralLink && (
            <>
              <form>
                <div className={styles['form-input']}>
                  <input type="text" className="border" disabled value={referralLink} />
                  <Button withGradient onClick={(e) => handleCopyReferralLink(e)} extraClasses={styles.btn}>
                    {copied ? t('buttonCopyCopied', 'Gekopieerd!') : t('buttonCopy', 'Kopiëren')}
                  </Button>
                </div>
              </form>
              <div className={styles['sharing-buttons']}>
                <EmailShareButton
                  className={styles['button-share']}
                  resetButtonStyle={false}
                  url={referralLink}
                  subject={messageShareTitle}
                  body={messageShare}
                >
                  <EmailIcon size={40} bgStyle={{ fill: 'none' }} />
                </EmailShareButton>
                <TwitterShareButton className={styles['button-share']} resetButtonStyle={false} url={referralLink} title={messageShare}>
                  <TwitterIcon size={40} bgStyle={{ fill: 'none' }} />
                </TwitterShareButton>
                <FacebookShareButton className={styles['button-share']} resetButtonStyle={false} url={referralLink} quote={messageShare}>
                  <FacebookIcon size={40} bgStyle={{ fill: 'none' }} />
                </FacebookShareButton>
                <WhatsappShareButton className={styles['button-share']} resetButtonStyle={false} url={referralLink} title={messageShare}>
                  <WhatsappIcon size={40} bgStyle={{ fill: 'none' }} />
                </WhatsappShareButton>
              </div>
            </>
          )}
        </div>
      )}
      <div className={styles.description}>
        {parse(
          t(
            'ctaDescription',
            '<p><strong>Overtuig een vriend van Bolt en maak samen kans op een e-bike.</strong> Samen, dat betekent één e-bike voor jou én één e-bike voor jouw vriend. 2 e-bikes dus! Wij geven bovendien 2 e-bikes weg per 50 deelnemers. Dat is <strong>1 kans op 50 om te winnen</strong>. De moeite!</p>'
          )
        )}
      </div>
    </div>
  )
}

export default ReferralForm
